import { Check } from "lucide-react"
import { Controller } from "react-hook-form"

import { FileUpload } from "@/components/ui/file-upload"
import { FormSectionHeader } from "@/components/ui/form-section-header"
import { Label } from "@/components/ui/label"

import { ExistingProductAssets } from "./ExistingProductAssets"
import type { ProductAssetFieldProps } from "./product-field-types"

export function ProductAssetFields({
  control,
  labels,
  errors,
  setValue,
  imageFiles,
  primaryImageIndex,
  showFiles,
  existingImages,
  existingTechSpec,
  onDeleteImage,
}: ProductAssetFieldProps) {
  return (
    <section>
      <FormSectionHeader number="03" title="DIGITAL ASSETS" />

      {showFiles && (
        <ExistingProductAssets
          labels={labels}
          existingImages={existingImages}
          existingTechSpec={existingTechSpec}
          onDeleteImage={onDeleteImage}
        />
      )}

      <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
        {showFiles && (
          <>
            <div className="space-y-6">
              <Label className="label-industrial">{labels.images}</Label>
              <Controller
                name="imageFiles"
                control={control}
                render={({ field }) => (
                  <FileUpload
                    id="product-images"
                    label={labels.uploadImages}
                    accept="image/*"
                    multiple={true}
                    value={field.value}
                    onChange={(files) => {
                      field.onChange(files)
                      if (primaryImageIndex >= files.length) {
                        setValue("primaryImageIndex", Math.max(0, files.length - 1))
                      }
                    }}
                    maxFiles={5}
                  />
                )}
              />
              {errors.imageFiles && (
                <p className="mt-1 text-xs font-bold uppercase tracking-wide text-destructive">
                  {errors.imageFiles.message as string}
                </p>
              )}

              {imageFiles.length > 0 && (
                <div className="mt-6 space-y-4">
                  <Label className="text-xs font-black uppercase tracking-widest text-muted-foreground/60">
                    {labels.selectPrimary}
                  </Label>
                  <div className="flex flex-wrap gap-3">
                    {imageFiles.map((file, index) => (
                      <div
                        key={index}
                        onClick={() => setValue("primaryImageIndex", index)}
                        className={`group relative h-20 w-20 cursor-pointer overflow-hidden rounded-lg border-2 transition-all duration-300 ${
                          primaryImageIndex === index
                            ? "border-primary shadow-lg ring-4 ring-primary/10"
                            : "border-border opacity-60 hover:border-foreground/30 hover:opacity-100"
                        }`}
                      >
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Preview ${index}`}
                          className="h-full w-full object-cover transition-transform group-hover:scale-110"
                          onLoad={(event) =>
                            URL.revokeObjectURL((event.target as HTMLImageElement).src)
                          }
                        />
                        {primaryImageIndex === index && (
                          <div className="absolute inset-0 flex items-center justify-center bg-primary/5">
                            <Check className="h-6 w-6 text-primary drop-shadow-md" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-6">
              <Label className="label-industrial">{labels.techSpec}</Label>
              <Controller
                name="technicalSpecFile"
                control={control}
                render={({ field }) => (
                  <FileUpload
                    id="tech-spec"
                    label={labels.uploadSpec}
                    accept=".pdf"
                    multiple={false}
                    value={field.value || []}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </>
        )}
      </div>
    </section>
  )
}
